import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { Session, User } from '@supabase/supabase-js';
import type { UserProfile, AppRole, AuthUser, AuthState } from '@/types/auth';
import { setSentryUser } from '@/lib/sentry';

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata: { full_name: string; department_id?: string; employee_id?: string }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // isAuthReady becomes true only when auth state is fully resolved:
  // - Either we have both session AND user data loaded
  // - Or we've confirmed there's no session (logged out state)
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Track initialization state to prevent race conditions
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  // Track current user for use in callbacks without causing re-renders
  const userRef = useRef<AuthUser | null>(null);
  // Lock to prevent cascading auth state updates (fixes React Error #185 in PWA)
  const isProcessingAuthEventRef = useRef(false);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const fetchUserData = useCallback(async (authUser: User): Promise<AuthUser | null> => {
    try {
      // Fetch profile - use maybeSingle() to gracefully handle 0 rows (e.g., stale session)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      // Only log actual errors, not "no rows" which is now handled by maybeSingle()
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        // Throw to trigger retry logic for network errors
        throw new Error(`Profile fetch failed: ${profileError.message}`);
      }

      // Handle case where profile doesn't exist yet (new user or stale session)
      // This is NOT a network error, so return null without throwing
      if (!profile) {
        console.warn('No profile found for user:', authUser.id);
        return null;
      }

      // Fetch roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authUser.id);

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        // Throw to trigger retry logic for network errors
        throw new Error(`Roles fetch failed: ${rolesError.message}`);
      }

      return {
        id: authUser.id,
        email: authUser.email || '',
        profile: profile as UserProfile,
        roles: (roles || []).map((r) => r.role as AppRole),
      };
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      throw error; // Re-throw to allow retry logic to handle it
    }
  }, []);

  // Phase 3: Retry logic with exponential backoff for network failures
  const fetchUserDataWithRetry = useCallback(async (
    authUser: User,
    retries = 3
  ): Promise<AuthUser | null> => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const result = await fetchUserData(authUser);
        // Success or profile doesn't exist (null without error)
        return result;
      } catch (error) {
        const isLastAttempt = attempt === retries - 1;

        if (isLastAttempt) {
          console.error('[Auth] All retry attempts failed:', error);
          return null;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = 1000 * Math.pow(2, attempt);
        console.warn(`[Auth] Fetch attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    return null;
  }, [fetchUserData]);

  const refreshProfile = useCallback(async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) {
      const userData = await fetchUserDataWithRetry(authUser);
      if (userData) {
        setUser(userData);
      }
    }
  }, [fetchUserDataWithRetry]);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        // For initial load, let getSession handle it to prevent race condition
        // Only handle subsequent auth changes (SIGNED_IN, SIGNED_OUT, etc.)
        if (!isInitializedRef.current) {
          return;
        }

        // Lock to prevent cascading auth state updates (fixes React Error #185 in PWA)
        // Skip if we're already processing an auth event to prevent infinite loops
        if (isProcessingAuthEventRef.current) {
          console.log('[AUTH] Skipping event - already processing:', event);
          return;
        }
        isProcessingAuthEventRef.current = true;

        // SILENT HANDLING: Token refresh should not disrupt the UI
        // This prevents the completion flow from losing state when users switch tabs
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AUTH] TOKEN_REFRESHED - handling silently');
          setSession(currentSession);
          isProcessingAuthEventRef.current = false;
          return;
        }

        // SILENT HANDLING: If we already have a session and get SIGNED_IN,
        // this is a websocket reconnection after tab switch, not a new login.
        // Handle silently to preserve UI state (selfies, form data, etc.)
        if (event === 'SIGNED_IN' && session) {
          console.log('[AUTH] SIGNED_IN with existing session - handling silently (reconnection)');
          setSession(currentSession);
          // Optionally refresh user data in background without unmounting
          if (currentSession?.user) {
            fetchUserDataWithRetry(currentSession.user).then((userData) => {
              if (userData) {
                setUser(userData);
              }
              isProcessingAuthEventRef.current = false;
            }).catch(() => {
              isProcessingAuthEventRef.current = false;
            });
          } else {
            isProcessingAuthEventRef.current = false;
          }
          return;
        }

        setSession(currentSession);

        if (currentSession?.user) {
          // Phase 2: Check if this is initial authentication or a session refresh
          // Only set isAuthReady=false for INITIAL authentication (when user was null)
          // This prevents unmounting children during session reconnection after tab switch
          const isInitialAuth = !userRef.current;

          if (isInitialAuth) {
            // INITIAL AUTH: User was not previously logged in
            // Need to set isAuthReady=false while we load their data
            setIsAuthReady(false);
          }
          // For session refresh (user already exists), DON'T set isAuthReady=false
          // This preserves UI state and prevents component unmounting

          // Defer Supabase calls with setTimeout to prevent deadlock
          setTimeout(() => {
            fetchUserDataWithRetry(currentSession.user).then((userData) => {
              if (userData) {
                setUser(userData);
                setSentryUser({ id: userData.id, email: userData.email });
              }

              // Only change isAuthReady if this was initial auth
              if (isInitialAuth) {
                setIsAuthReady(true);
              }
              isProcessingAuthEventRef.current = false;
            }).catch(() => {
              isProcessingAuthEventRef.current = false;
            });
          }, 0);
        } else {
          // User signed out - auth is ready (in logged-out state)
          setUser(null);
          setSentryUser(null);
          setIsAuthReady(true);
          isProcessingAuthEventRef.current = false;
        }
      }
    );

    // THEN check for existing session - this handles initial load
    supabase.auth.getSession().then(async ({ data: { session: existingSession } }) => {
      // Handle "Remember me" check during initialization
      // If user had "Remember me" unchecked and browser was closed, sign them out
      const rememberMe = localStorage.getItem('rememberMe');
      const wasActive = sessionStorage.getItem('session_active');

      if (existingSession && rememberMe === 'false' && !wasActive) {
        // Session exists but user didn't want to be remembered and browser was restarted
        await supabase.auth.signOut({ scope: 'global' });
        localStorage.removeItem('rememberMe');
        isInitializedRef.current = true;
        setIsLoading(false);
        setIsAuthReady(true); // Ready in logged-out state
        return;
      }

      setSession(existingSession);
      if (existingSession?.user) {
        fetchUserDataWithRetry(existingSession.user).then((userData) => {
          setUser(userData);
          // Set Sentry user context on initial load
          if (userData) {
            setSentryUser({ id: userData.id, email: userData.email });
          }
          // Mark as initialized BEFORE setting loading/ready states
          isInitializedRef.current = true;
          setIsLoading(false);
          setIsAuthReady(true); // Ready with session and user data
        });
      } else {
        // No session - mark initialized and stop loading
        isInitializedRef.current = true;
        setIsLoading(false);
        setIsAuthReady(true); // Ready in logged-out state
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserDataWithRetry]);

  // Handle "Remember me" - mark session as active while tab is open
  // Note: The actual sign-out for non-remembered sessions is handled during initialization
  // in the getSession callback above, not here, to avoid race conditions
  useEffect(() => {
    if (session) {
      // Mark session as active in sessionStorage
      // This persists only while the browser is open
      sessionStorage.setItem('session_active', 'true');
    }
  }, [session]);

  const signIn = async (email: string, password: string) => {
    // Clear any stale session before attempting sign-in to prevent auth state conflicts
    // This handles cases where session expired but local state wasn't properly cleared
    try {
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        await supabase.auth.signOut({ scope: 'local' });
        setUser(null);
        setSession(null);
      }
    } catch {
      // Ignore errors from clearing stale session - proceed with sign-in attempt
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (
    email: string,
    password: string,
    metadata: { full_name: string; department_id?: string; employee_id?: string }
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: metadata.full_name,
          department_id: metadata.department_id,
          employee_id: metadata.employee_id,
          role: 'employee', // Default role for new signups
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut({ scope: 'global' });
    setUser(null);
    setSession(null);
    setSentryUser(null);
    // Clear any persisted auth-related data to prevent stale session reuse on mobile
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('savedEmail');
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const isApproved = user?.profile?.is_approved ?? false;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        isAuthReady,
        isApproved,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
